import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, FormEvent } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Label, Larger } from '@/components/Forms';
import CurrencyField from '@/components/Forms/CurrencyField';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import { AddButtonHolder } from '@/components/Testing';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { selectTeamTestingProperties } from '@/selectors';
import { api } from '@/utils/api';
import { campaignAutomationsOptions } from '@/utils/data/testing';
import {
  OptimisationProjectNetworkEnum,
  OptimisationTestTypeEnum,
} from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { getAudienceSegments } from '@/utils/optimisationTests';
import { RootState } from '@/utils/store';

import AuthorField from './AuthorField';
import FinalisationSetFields from './FinalisationSetFields';
import TestingAudienceField from './TestingAudienceField';
import TestingProjectName from './TestingProjectName';
import TestingPropertiesFields from './TestingPropertiesFields';

const StyledTestingPropertiesFields = styled(TestingPropertiesFields)`
  margin: 32px 0 24px;
`;

const StyledAddButtonHolder = styled(AddButtonHolder)`
  > button {
    width: 100%;
  }
`;

const AddTitleButton = styled.button`
  display: inline-flex;
  align-items: center;
  margin-top: -8px;
  svg {
    margin-right: 8px;
  }
`;

const TitleFieldWrap = styled.div`
  position: relative;
  grid-column: span 2;
`;

const RemoveTitlebutton = styled(Text)`
  top: 0;
  right: 0;
  position: absolute;
  text-decoration: underline;
`;

const TitleInputBadge = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.purpleLighter};
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 33px;
  top: 0;
`;

export interface FormDataT {
  max_cpv: string;
  campaign_budget: string;
  author: string;
  batch: number;
  campaign_start_date: string;
  campaign_end_date: string;
  testing_audience_id?: number;
  titles: Array<{ title: string }>;
  title: string;
  audience_segment: string;
  campaign_automations: string;
  multitest_enabled: boolean;
  paste_file_name: boolean;
  sets: Array<{
    video: string;
    title: string;
    videoFilename: string;
    variations: Array<{
      name: string;
      imageFile: File | null;
      image: string;
    }>;
  }>;
}

interface Props {
  onClose: () => void;
  network: OptimisationProjectNetworkEnum;
  projectTitle: string;
  isForTitles?: boolean;
}

const FinalisationFormModal: FC<Props> = ({
  onClose,
  network,
  projectTitle,
  isForTitles = false,
}) => {
  const { id, teamId } = useParams();

  const theme = useTheme();

  const { testingSettings, eytvTestingAudience } = useSelector(
    (state: RootState) => selectTeamTestingProperties(state, teamId!),
  );

  const userName = useSelector(
    (state: RootState) => state.auth.user?.name ?? '',
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const audienceSegments = getAudienceSegments(
    testingSettings?.google_ads_client_id ?? '',
  );

  const methods = useForm<FormDataT>({
    defaultValues: {
      max_cpv: testingSettings?.finalisation_max_cpv?.toString() ?? '',
      campaign_budget:
        testingSettings?.finalisation_campaign_budget?.toString() ?? '',
      author: userName,
      batch: 1,
      campaign_start_date: DateTime.now().toISODate(),
      campaign_end_date: DateTime.now().plus({ days: 1 }).toISODate(),
      paste_file_name: testingSettings?.finalisation_paste_file_name ?? false,
      title: '',
      titles: [
        {
          title: '',
        },
      ],
      audience_segment: '',
      multitest_enabled: false,
      campaign_automations: campaignAutomationsOptions[0].value,
      sets: isForTitles
        ? [
            {
              video: '',
              title: '',
              videoFilename: '',
              variations: [],
            },
          ]
        : [],
      testing_audience_id: eytvTestingAudience ?? 0,
    },
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = methods;

  const {
    fields: sets,
    append: appendSet,
    remove: removeSet,
  } = useFieldArray({
    control,
    name: 'sets',
  });

  const {
    fields: titleFields,
    append: appendTitle,
    remove: removeTitle,
  } = useFieldArray({
    control,
    name: 'titles',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();

    handleSubmit(async ({ sets, titles, ...data }) => {
      try {
        const variations = sets.reduce<
          Array<{ name: string; title: string; image: string; video: string }>
        >((acc, { title, video, videoFilename, variations: setVariations }) => {
          if (!isForTitles) {
            const formattedVariations = setVariations.map(
              ({ name, image, imageFile }, variationIndex) => ({
                name: name
                  .replace('{video}', videoFilename)
                  .replace(
                    '{thumb}',
                    data.paste_file_name
                      ? imageFile?.name ?? ''
                      : (variationIndex + 1).toString(),
                  ),
                title,
                image,
                video,
              }),
            );
            return [...acc, ...formattedVariations];
          }

          const formattedVariations: Array<{
            name: string;
            title: string;
            image: string;
            video: string;
          }> = [];

          titles.forEach(({ title }) => {
            if (!title) return;

            setVariations.forEach(
              ({ name, image, imageFile }, variationIndex) => {
                formattedVariations.push({
                  name: name
                    .replace('{video}', videoFilename)
                    .replace('{title}', title)
                    .replace(
                      '{thumb}',
                      data.paste_file_name
                        ? imageFile?.name ?? ''
                        : (variationIndex + 1).toString(),
                    ),
                  title,
                  image,
                  video,
                });
              },
            );
          });

          return [...acc, ...formattedVariations];
        }, []);

        const body = {
          optimisation_test_id: id,
          author: data.author,
          type: isForTitles
            ? OptimisationTestTypeEnum.TITLE
            : OptimisationTestTypeEnum.FINALISATION,
          multitest_enabled: data.multitest_enabled,
          batch: data.batch,
          campaign_start_date: data.campaign_start_date,
          campaign_end_date: data.campaign_end_date,
          ...(!!data.testing_audience_id && {
            testing_audience_id: data.testing_audience_id,
          }),
          audience_segment: data.audience_segment,
          campaign_automations: data.campaign_automations,
          network,
          ...(network === OptimisationProjectNetworkEnum.YOUTUBE && {
            campaign_budget: data.campaign_budget,
            max_cpv: data.max_cpv,
          }),
          variations,
        };

        const { error } = await api.post<typeof body, { error?: string }>(
          '/v1/optimisation-test-units/finalisation',
          body,
        );

        if (error) {
          return alerts.error(error);
        }

        queryClient.invalidateQueries(['optimisation_tests'], {
          exact: false,
        });

        alerts.success(`Finalisation have been successfully created`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  return (
    <Modal>
      <SidePanel heading="Upload finalisation tests" onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <FormProvider {...methods}>
            <SidePanelContent>
              <Larger>
                <TestingProjectName projectTitle={projectTitle} />
              </Larger>
              {network === OptimisationProjectNetworkEnum.YOUTUBE && (
                <Larger>
                  <StyledTestingPropertiesFields
                    title="Finalisation testing properties"
                    withoutBorder
                    hasErrors={!!errors.max_cpv || !!errors.campaign_budget}
                  >
                    <Larger>
                      <Label htmlFor="thumbsMaxCpv">
                        Finalisation maximum CPV
                      </Label>
                      <CurrencyField<FormDataT>
                        control={control}
                        name="max_cpv"
                        inputId="thumbsMaxCpv"
                        placeholder="Add value"
                      />
                      {!!errors.max_cpv && (
                        <ValidationMessage>
                          {errors.max_cpv.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                    <Larger>
                      <Label htmlFor="campaignBudgetInput">
                        Finalisation campaign budget
                      </Label>
                      <CurrencyField<FormDataT>
                        control={control}
                        name="campaign_budget"
                        inputId="campaignBudgetInput"
                        placeholder="Add value"
                      />
                      {!!errors.campaign_budget && (
                        <ValidationMessage>
                          {errors.campaign_budget.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                    <FormField<FormDataT>
                      control={control}
                      id="pasteFileName"
                      name="paste_file_name"
                      type="switch"
                      headline="Paste File Name"
                      description="Automatically apply file name of every uploaded thumbnail/video to the variation name input field"
                    />
                  </StyledTestingPropertiesFields>
                </Larger>
              )}
              <FormLayout>
                {network === OptimisationProjectNetworkEnum.YOUTUBE && (
                  <>
                    <FormField<FormDataT>
                      control={control}
                      id="mutlitestSwitch"
                      name="multitest_enabled"
                      type="switch"
                      headline="Multitest"
                      description="Each video and thumbnail can be tested up to three times. Outcome will show individual results of each video as well as their average, which will provide a more accurate overall result. However, this method can cost up to three times more."
                    />
                    <TestingAudienceField<FormDataT>
                      control={control}
                      error={errors.testing_audience_id}
                    />
                    <FormField<FormDataT, string>
                      control={control}
                      label="Audience Segment"
                      type="select"
                      name="audience_segment"
                      options={audienceSegments}
                    />
                  </>
                )}
                {isForTitles ? (
                  <>
                    {titleFields.map((field, i) => (
                      <TitleFieldWrap>
                        <TitleInputBadge>
                          <Text as="span" $color="white" $type="extraSmall">
                            {i + 1}
                          </Text>
                        </TitleInputBadge>
                        {titleFields.length > 1 && (
                          <RemoveTitlebutton
                            as="button"
                            type="button"
                            $color="error"
                            $type="smallHeadline"
                            onClick={() => removeTitle(i)}
                          >
                            Remove
                          </RemoveTitlebutton>
                        )}
                        <FormField<FormDataT>
                          control={control}
                          label="Title"
                          type="text"
                          placeholder="Add title"
                          name={`titles.${i}.title`}
                          key={field.id}
                        />
                      </TitleFieldWrap>
                    ))}
                    <AddTitleButton
                      type="button"
                      onClick={() => appendTitle({ title: '' })}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="8"
                          fill={theme.colors.purple}
                          fillOpacity="0.12"
                        />
                        <path
                          d="M8 5V11"
                          stroke={theme.colors.purple}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 8H11"
                          stroke={theme.colors.purple}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <Text as="span" $type="smallHeadline">
                        Add additional title
                      </Text>
                    </AddTitleButton>
                  </>
                ) : (
                  <FormField<FormDataT>
                    control={control}
                    label="Title"
                    type="text"
                    name="title"
                  />
                )}
                <FormField<FormDataT, number>
                  control={control}
                  label="Batch"
                  type="select"
                  name="batch"
                  options={Array.from({ length: 11 }, (_, i) => ({
                    value: i,
                    label: `T${i}`,
                  }))}
                />
                <AuthorField<FormDataT> control={control} name="author" />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Start Date"
                  type="datepicker"
                  name="campaign_start_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign End Date"
                  type="datepicker"
                  name="campaign_end_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Automations"
                  type="select"
                  options={campaignAutomationsOptions}
                  name="campaign_automations"
                />
              </FormLayout>
              {sets.map((set, index) => (
                <FinalisationSetFields
                  isForTitles={isForTitles}
                  index={index}
                  key={set.id}
                  control={control}
                  removeSet={removeSet}
                  setValue={setValue}
                />
              ))}
              <StyledAddButtonHolder>
                <Button
                  type="button"
                  $type="colorized"
                  onClick={() =>
                    appendSet({
                      video: '',
                      videoFilename: '',
                      title: getValues('title'),
                      variations: [],
                    })
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 2V14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 8H14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add set
                </Button>
              </StyledAddButtonHolder>
            </SidePanelContent>
          </FormProvider>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Upload
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FinalisationFormModal;
